import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Zap, MapPin, Users } from 'lucide-react';

// Mock templates (später aus Backend)
const MOCK_TEMPLATES = [
  {
    id: 'jga-munich-classic',
    name: 'Munich Classic',
    city: 'München',
    icon: '🍺',
    messages: ['Auf geht\'s nach München!', 'Biergarten - hier geht\'s los!', 'Prosit!'],
    structure: 'classic',
  },
  {
    id: 'jga-berlin-wild',
    name: 'Berlin Wild',
    city: 'Berlin',
    icon: '🎉',
    messages: ['Berlin ist am Start!', 'Club-Nacht!', 'Noch eine Runde?'],
    structure: 'party',
  },
  {
    id: 'jga-hamburg-chic',
    name: 'Hamburg Chic',
    city: 'Hamburg',
    icon: '⛵',
    messages: ['Hamburg wartet!', 'Hafenrundfahrt geplant!', 'Was für eine Stadt!'],
    structure: 'elegant',
  },
  {
    id: 'jga-cologne-party',
    name: 'Cologne Party',
    city: 'Köln',
    icon: '🎭',
    messages: ['Cologne ist Wahnsinn!', 'Karneval-Feeling!', 'Alaaf!'],
    structure: 'fun',
  },
  {
    id: 'jga-frankfurt-modern',
    name: 'Frankfurt Modern',
    city: 'Frankfurt',
    icon: '🌃',
    messages: ['Frankfurt moderne JGA!', 'Mainufer Party!', 'Modern und cool!'],
    structure: 'modern',
  },
  {
    id: 'jga-zurich-luxury',
    name: 'Zurich Luxury',
    city: 'Zürich',
    icon: '💎',
    messages: ['Zürich Luxus-Wochenende!', 'Champagne & Style!', 'Unforgettable!'],
    structure: 'luxury',
  },
];

export default function DemoJGATemplateSelector() {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);

  const cities = [...new Set(MOCK_TEMPLATES.map(t => t.city))];
  const filteredTemplates = selectedCity
    ? MOCK_TEMPLATES.filter(t => t.city === selectedCity)
    : MOCK_TEMPLATES;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg p-6 mb-6"
      >
        <h2 className="text-2xl font-bold mb-2">✨ JGA+ Event-Vorlagen</h2>
        <p className="text-pink-100">
          Wähle eine vorgefertigte Vorlage oder erstelle dein eigenes Event-Konzept
        </p>
      </motion.div>

      {/* City Filter */}
      <div className="mb-6 bg-white rounded-lg p-6 shadow-lg">
        <h3 className="font-bold mb-4 flex items-center gap-2">
          <MapPin size={20} /> Filtern nach Stadt
        </h3>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCity(null)}
            className={`px-4 py-2 rounded-lg transition ${
              selectedCity === null
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 hover:bg-gray-200'
            }`}
          >
            Alle Städte
          </button>
          {cities.map((city) => (
            <button
              key={city}
              onClick={() => setSelectedCity(city)}
              className={`px-4 py-2 rounded-lg transition ${
                selectedCity === city
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              {city}
            </button>
          ))}
        </div>
      </div>

      {/* Template Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {filteredTemplates.map((template) => (
          <motion.button
            key={template.id}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setSelectedTemplate(template)}
            className={`text-left p-6 rounded-lg border-2 transition ${
              selectedTemplate?.id === template.id
                ? 'border-purple-600 bg-purple-50'
                : 'border-gray-200 hover:border-purple-300 bg-white'
            } shadow-md`}
          >
            {/* Icon */}
            <div className="text-4xl mb-3">{template.icon}</div>

            {/* Name & City */}
            <h4 className="font-bold text-lg mb-1">{template.name}</h4>
            <p className="text-sm text-gray-600 mb-3 flex items-center gap-1">
              <MapPin size={14} /> {template.city}
            </p>

            {/* Preview Messages */}
            <div className="space-y-1 mb-3">
              {template.messages.slice(0, 2).map((msg, idx) => (
                <p key={idx} className="text-xs text-gray-600 line-clamp-1">
                  💬 &quot;{msg}&quot;
                </p>
              ))}
            </div>

            {/* Style Tag */}
            <div className="inline-block px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
              {template.structure}
            </div>

            {/* Selection Check */}
            {selectedTemplate?.id === template.id && (
              <div className="mt-3 text-purple-600 font-bold text-sm">✓ Ausgewählt</div>
            )}
          </motion.button>
        ))}
      </div>

      {/* Selected Template Details */}
      {selectedTemplate && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg p-8 shadow-lg border-2 border-purple-200"
        >
          <h3 className="text-2xl font-bold mb-4">
            {selectedTemplate.icon} {selectedTemplate.name} - Vorlage Details
          </h3>

          {/* Info */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-gray-600 text-sm">Stadt</p>
              <p className="font-bold text-lg">{selectedTemplate.city}</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-gray-600 text-sm">Stil</p>
              <p className="font-bold text-lg capitalize">{selectedTemplate.structure}</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-gray-600 text-sm">Nachrichten</p>
              <p className="font-bold text-lg">{selectedTemplate.messages.length}</p>
            </div>
          </div>

          {/* Template Messages */}
          <div className="mb-6">
            <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
              <Zap size={20} /> Vorlage-Nachrichten
            </h4>
            <div className="space-y-2">
              {selectedTemplate.messages.map((msg, idx) => (
                <div key={idx} className="bg-purple-50 p-3 rounded-lg border-l-4 border-purple-400">
                  <p className="text-gray-800">&quot;{msg}&quot;</p>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button className="flex-1 bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition font-bold">
              ✏️ Vorlage bearbeiten
            </button>
            <button className="flex-1 bg-pink-600 text-white py-3 rounded-lg hover:bg-pink-700 transition font-bold">
              🚀 Jetzt starten
            </button>
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4 text-sm text-blue-900">
            <p>
              <strong>💡 Tipp:</strong> Die Vorlage ist ein Startpunkt. Du kannst alle Nachrichten
              und Event-Details anpassen.
            </p>
          </div>
        </motion.div>
      )}

      {/* Empty State */}
      {!selectedTemplate && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Zap size={48} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 text-lg">
            Wähle eine Vorlage oben aus, um zu beginnen
          </p>
        </div>
      )}
    </div>
  );
}
